import React, { useEffect, useState } from 'react'
import { ConsultationPrice, Psychologist } from '../../types'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core'
import { DataGrid, GridColDef } from '@material-ui/data-grid'
import { client } from '../../client'

interface TimeRegistrationStats {
  id: string
  psychologistId: number
  psychologistName: string
  productCode: number
  count: number
  year: number
}

const TimeRegistrationStatsPage: React.FC = () => {
  const [stats, setStats] = useState<TimeRegistrationStats[]>([])
  const [loading, setLoading] = useState(true)
  const [psychologists, setPsychologists] = useState<Psychologist[]>([])
  const [consultationPrices, setConsultationPrices] = useState<
    ConsultationPrice[]
  >([])
  const [selectedPsychologist, setSelectedPsychologist] = useState<number | ''>(
    ''
  )
  const [selectedCode, setSelectedCode] = useState<string>('')
  const [selectedYear, setSelectedYear] = useState<number | ''>(
    new Date().getFullYear()
  )
  const [availableYears, setAvailableYears] = useState<number[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [psychologistsResponse, pricesResponse, initialStats] =
          await Promise.all([
            client.getPsychologistOptions(),
            client.getConsultationCodes(),
            client.getTimeRegistrationStats()
          ])
        setPsychologists(psychologistsResponse)
        setConsultationPrices(pricesResponse)
        const years: number[] = Array.from(
          new Set(initialStats.data.map((stat) => stat.year))
        )
          .map(Number)
          .sort((a: number, b: number) => b - a)
        setAvailableYears(years)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        const response = await client.getTimeRegistrationStats(
          selectedPsychologist || undefined,
          selectedCode || undefined,
          selectedYear || undefined,
          pagination.page,
          pagination.pageSize
        )
        const statsWithIds = response.data.map((stat, index) => ({
          ...stat,
          id: `${stat.psychologistId}-${stat.productCode}-${stat.year}-${index}`
        }))
        setStats(statsWithIds)
        setPagination((prev) => ({
          ...prev,
          total: response.total
        }))
      } catch (error) {
        console.error('Error fetching time registration stats:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [
    selectedPsychologist,
    selectedCode,
    selectedYear,
    pagination.page,
    pagination.pageSize
  ])

  const getFullName = (psychologist: Psychologist) => {
    if (!psychologist.user) {
      return ''
    }
    const { firstName, infix, lastName } = psychologist.user
    return [firstName, infix, lastName].filter(Boolean).join(' ')
  }

  const sortedPsychologists = [...psychologists].sort((a, b) => {
    const nameA = getFullName(a).toLowerCase()
    const nameB = getFullName(b).toLowerCase()
    return nameA.localeCompare(nameB)
  })

  const uniqueConsultationCodes = Array.from(
    new Set(consultationPrices.map((price) => price.code))
  ).sort((a, b) => a.localeCompare(b))

  const columns: GridColDef[] = [
    {
      field: 'psychologistName',
      headerName: 'Psychologist',
      flex: 1,
      sortable: false
    },
    {
      field: 'productCode',
      headerName: 'Consultation Code',
      flex: 1,
      sortable: false
    },
    { field: 'year', headerName: 'Year', flex: 1, sortable: false },
    {
      field: 'count',
      headerName: 'Number of Registrations',
      flex: 1,
      sortable: false
    }
  ]

  const handleClearFilters = () => {
    setSelectedPsychologist('')
    setSelectedCode('')
    setSelectedYear('')
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  if (loading && !stats.length) {
    return <Typography>Loading...</Typography>
  }

  return (
    <Box p={3} style={{ width: '100%', overflowX: 'auto', paddingTop: '5rem' }}>
      <Typography variant="h4" gutterBottom>
        Time Registration Count by Price Code
      </Typography>
      <Grid container spacing={2} style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Psychologist</InputLabel>
            <Select
              value={selectedPsychologist}
              onChange={(e) =>
                setSelectedPsychologist(e.target.value as number)
              }
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value="">
                <em>All Psychologists</em>
              </MenuItem>
              {sortedPsychologists.map((psychologist) => (
                <MenuItem key={psychologist.id} value={psychologist.id}>
                  {getFullName(psychologist)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Consultation Code</InputLabel>
            <Select
              value={selectedCode}
              onChange={(e) => setSelectedCode(e.target.value as string)}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value="">
                <em>All Codes</em>
              </MenuItem>
              {uniqueConsultationCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value as number)}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value="">
                <em>All Years</em>
              </MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={1}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClearFilters}
            style={{ height: '100%', width: '100%' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
      <div style={{ minWidth: '100%' }}>
        <DataGrid
          rows={stats}
          columns={columns}
          pageSize={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableSelectionOnClick
          loading={loading}
          autoHeight
          paginationMode="server"
          rowCount={pagination.total}
          page={pagination.page - 1}
          onPageChange={(newPage) =>
            setPagination((prev) => ({ ...prev, page: newPage + 1 }))
          }
          onPageSizeChange={(newPageSize) =>
            setPagination((prev) => ({
              ...prev,
              pageSize: newPageSize,
              page: 1
            }))
          }
        />
      </div>
    </Box>
  )
}

export default TimeRegistrationStatsPage
